<template>
  <div class="h-100 d-flex flex-column">
    <!-- Row: title and resize button -->
    <div class="row f-row markets-chart-title d-flex align-items-center">
      <div class="col col-6">Seasonal Trends Overview</div>
      <slot name="spot"></slot>
    </div>
    <!-- Row: controls and labels -->
    <div
      class="row f-row"
      id="seasonalControls"
      style="vertical-align: middle!important; padding: 10px 0 0 50px!important;"
    >
      <div class="col col-2 txt-label-primary-2 text-center">
        <div
          class="btn-toolbar"
          role="toolbar"
          aria-label="Toolbar with button groups"
          style="width:100%"
        >
          <div class="btn-group btn-group-toggle btn-block" role="group">
            &nbsp;
            <button
              type="button"
              class="btn btn-candle"
              v-on:click="_pickedDateSeasonality('3Y')"
              v-bind:class="[pickedDateSeasonality === '3Y' ? 'active' : '']"
            >
              3Y
            </button>
            <button
              type="button"
              class="btn btn-candle"
              v-on:click="_pickedDateSeasonality('5Y')"
              v-bind:class="[pickedDateSeasonality === '5Y' ? 'active' : '']"
            >
              5Y
            </button>
            <button
              type="button"
              class="btn btn-candle"
              v-on:click="_pickedDateSeasonality('7Y')"
              v-bind:class="[pickedDateSeasonality === '7Y' ? 'active' : '']"
            >
              7Y
            </button>
            <button
              type="button"
              class="btn btn-candle"
              v-on:click="_pickedDateSeasonality('10Y')"
              v-bind:class="[pickedDateSeasonality === '10Y' ? 'active' : '']"
            >
              10Y
            </button>
          </div>
        </div>
      </div>
      <div class="col col-2"></div>
      <div
        class="col txt-label-primary-2"
        :class="[screenWidth > 1320 ? 'col-4' : 'col-6']"
        style="background: var(--background); border: 1px solid var(--lightbackground)"
      >
        <table
          class="table table-sm table-borderless table-data-controls"
          style="width: 100%;"
        >
          <tbody>
            <tr class="txt-label-primary-5">
              <td class="txt-label-primary-5 text-center">
                Week of year:
              </td>
              <td class="txt-label-primary-5 text-center">
                Day month:
              </td>
              <td class="txt-label-primary-5 text-center">
                Year:
              </td>
              <td class="txt-label-primary-5 text-center">
                Value:
              </td>
              <td class="txt-label-primary-5 text-center">
                Average:
              </td>
            </tr>
            <tr>
              <td class="txt-label-primary">
                <div id="seasonalWeek" class="candle-hover-info text-center">
                  -
                </div>
              </td>
              <td class="txt-label-primary">
                <div
                  id="seasonalWeekDate"
                  class="candle-hover-info text-center"
                >
                  -
                </div>
              </td>
              <td class="txt-label-primary">
                <div id="hoverYear" class="candle-hover-info text-center">
                  -
                </div>
              </td>
              <td class="txt-label-primary">
                <div id="hoverVal" class="candle-hover-info  text-center">
                  -
                </div>
              </td>
              <td class="txt-label-primary">
                <div id="seasonalAve" class="candle-hover-info text-center">
                  -
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- Row: legend -->
    <div class="row f-row container-legends">
      <div class="col col-1"></div>
      <div class="col col-10 text-left" style="z-index: 100;">
        <table
          class="table table-sm table-responsive table-borderless table-market-legend"
          style="width: 100%;"
        >
          <tbody>
            <tr>
              <td width="40px"></td>
              <td
                class="text-center"
                v-for="(name, index) in seasonalChartData.series.names"
                :key="index"
              >
                <i
                  class="fa fa-minus mr-1"
                  :style="{ color: colorsSeasonal[index] }"
                ></i>
                {{ name }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- Row: Chart -->
    <div class="row f-row h-100">
      <div class="col col-12 text-center progress-div" v-if="showLoading">
        <QSLoading
          :isLoading="showLoading"
          :size="35"
          :colorTheme="colorTheme"
        />
      </div>
      <div
        class="col col-12 text-center"
        v-else-if="seasonalChartData.length === 0"
        style="position: absolute; left: 0; top: 45%; z-index: 10000000;"
      >
        Failed to fetch data.<br />Please refresh page or contact support.
      </div>
      <div class="col col-12 container-svg-seasonality" style="width:100%">
        <div
          class="h-100 d-flex flex-column"
          id="seasonality-chart-div"
          style="width: 100%;"
        >
          <svg id="svg-seasonality"></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SeasonalChart } from "@/_core/charts/SeasonalChart";
import { TotalCurrencyExposure } from "@/_core/data/TotalCurrencyExposure";
import { ProcessData } from "@/_core/data/ProcessData";
import { timeParseNull2 } from "@/_core/_helpers/_helpers";
import {
  colorSelection2,
  formatPercent,
  tYears
} from "@/_core/_helpers/_parametric_tools";
import { apiService } from "@/_services";
import { format, select } from "d3";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "Seasonality",
  components: { QSLoading },
  props: {
    chartDataRaw: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    dataReady: { required: true },
    renderSeasonality: { required: true },
    selectedCurrency: { required: true },
    showLoading: { required: true },
    spot: { required: true }
  },
  data() {
    return {
      // ========== Data =============
      ave: 0,
      colorsSeasonal: ["rgb(255, 224, 0)"],
      currencySpecs: [],
      formatPercent: formatPercent,
      initCurrencyDate: new Date(
        new Date().setFullYear(new Date().getFullYear() - 11)
      ),
      minMaxYAxisSeas: [0, 1000000000000],
      ninety: 0,
      percentileChartData: [],
      percentileDates: [],
      percentiles: [],
      pickedDateSeasonality: "10Y",
      processData_season: null,
      screenWidth: 0,
      seasonalChartData: { series: { names: ["AVERAGE"] } },
      seasonalChartDataRaw: [],
      selectedYearsSeasonality: 11,
      ten: 0,
      tod: 0,
      totalCurrencyExposure_season: null,
      tYearsSeasonality: tYears
    };
  },
  created() {
    try {
      this.currencies = this.$store.state.currencies.all.items;
    } catch (error) {
      console.log(`Error currencies ${error}`);
    }
    try {
      this.currencySpecs = this.$store.state.currencies.all.items;
    } catch (error) {
      console.log(`Error currencies ${error}`);
    }
    try {
      this.totalCurrencyExposure_season = new TotalCurrencyExposure(
        "",
        "api",
        apiService,
        this.spot,
        null,
        this.selectedDaysCandle,
        this.intervalCandle,
        0
      );
      this.processData_season = new ProcessData();
    } catch (error) {
      console.log(`Error data classes ${error}`);
    }

    this.currencyString = this.currencies[this.selectedCurrency]; // From PK to currency string
  },
  mounted() {
    if (this.chartDataRaw.length > 0) {
      this.prepareDataSeas();
    }

    let _this = this;
    this.updateScreenWidth();
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  filters: {
    formatNum: function(value) {
      if (value) {
        return format(",.4r")(value);
      }
    }
  },
  watch: {
    colorTheme() {
      this.prepareDataSeas();
    },
    dataReady() {
      this.prepareDataSeas();
    },
    renderSeasonality() {
      this.renderSeasonalMethod();
    },
    showLoading() {
      if (this.showLoading) {
        select("#seasonality-chart-div")
          .select("svg > *")
          .remove();
      }
    }
  },
  methods: {
    callWidth() {
      this.updateScreenWidth();
      // Event listener that triggers chart rendering on size change
      this.renderSeasonalMethod();
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    prepareDataSeas() {
      this.seasonalChartDataRaw = JSON.parse(JSON.stringify(this.chartDataRaw));
      this.seasonalChartDataRaw.forEach(function(d) {
        d.date = timeParseNull2(d.date);
      });
      this.seasonalChartDataRaw = this.totalCurrencyExposure_season.dataCompress(
        this.processData_season.toSlice(this.seasonalChartDataRaw, 2970),
        "weekYear",
        true
      ); // 11yrs, compress according to year's week number
      this.seasonalChartDataRaw = this.totalCurrencyExposure_season.parseSeasonalData(
        this.seasonalChartDataRaw
      );
      this.seasonalChartData = this.totalCurrencyExposure_season.transformDataSeas(
        this.seasonalChartDataRaw,
        this.selectedYearsSeasonality
      );
      this.minMaxYAxisSeas = [
        this.seasonalChartData.series.min,
        this.seasonalChartData.series.max
      ];
      this.renderSeasonalMethod();
    },
    _pickedDateSeasonality(paramInterval) {
      this.pickedDateSeasonality = paramInterval;
      this.selectedYearsSeasonality = this.tYearsSeasonality[
        this.pickedDateSeasonality
      ];
      this.seasonalChartData = this.totalCurrencyExposure_season.transformDataSeas(
        this.seasonalChartDataRaw,
        this.selectedYearsSeasonality
      );
      this.minMaxYAxisSeas = [
        this.seasonalChartData.series.min,
        this.seasonalChartData.series.max
      ];
      this.renderSeasonalMethod();
    },
    renderSeasonalMethod() {
      this.colorsSeasonal = colorSelection2(this.colorTheme).slice(
        -this.selectedYearsSeasonality - 1
      );
      const seasonalChart = new SeasonalChart(
        "seasonality-chart-div",
        "svg-seasonality",
        this.seasonalChartData,
        null,
        this.minMaxYAxisSeas,
        this.formatPercent,
        "seasonalControls",
        "seasonalWeek",
        "seasonalAve",
        this.colorsSeasonal,
        "hoverYear",
        "hoverVal"
      );

      seasonalChart.initVis();
    }
  }
};
</script>

<style scoped>
.container-legends {
  position: absolute;
  left: -5%;
  top: 14vh;
}
.container-svg-seasonality {
  height: 100%;
}
@media (max-width: 1400px) {
  .table-market-legend {
    margin-bottom: 0 !important;
    margin-top: 1rem !important;
  }
}
@media (min-width: 1440px) {
  .container-legends {
    top: 13vh;
  }
  .container-svg-seasonality {
    height: 98%;
  }
}
</style>
